import { PredictiveAnswerTypeDto } from "@advanced-sales-technologies/backend-client/models/PredictiveAnswerTypeDto";
import { TFunction } from "i18next";

import { FormConfigField, FormValues } from "@/types/DynamicForm.type";

enum target {
  REGION_COUNTRY = "REGION_COUNTRY",
  REGION_COUNTRY_INCLUDE = "REGION_COUNTRY_INCLUDE",
  REGION_COUNTRY_EXCLUDE = "REGION_COUNTRY_EXCLUDE",
  INDUSTRY_INCLUDE = "INDUSTRY_INCLUDE",
  INDUSTRY_EXCLUDE = "INDUSTRY_EXCLUDE",
  AUDIENCE_CHARACTERISTICS = "AUDIENCE_CHARACTERISTICS",
  HEADCOUNT = "HEADCOUNT",
  REVENUE = "REVENUE",
  COMPANY_AGE = "COMPANY_AGE",
  CONTACT_PERSON_ROLE_INCLUDE = "CONTACT_PERSON_ROLE_INCLUDE",
  CONTACT_PERSON_ROLE_FREE_FORM_INCLUDE = "CONTACT_PERSON_ROLE_FREE_FORM_INCLUDE",
  CONTACT_PERSON_ROLE_EXCLUDE = "CONTACT_PERSON_ROLE_EXCLUDE",
  CONTACT_PERSON_ROLE_FREE_FORM_EXCLUDE = "CONTACT_PERSON_ROLE_FREE_FORM_EXCLUDE",
  SIGNAL_INCLUDE = "SIGNAL_INCLUDE",
  NO_SIGNAL = "NO_SIGNAL",
  SIGNAL_EXCLUDE = "SIGNAL_EXCLUDE",
  TOOL = "TOOL",
  TOOL_FREE_FORM = "TOOL_FREE_FORM",
  CLIENT = "CLIENT",
  COMPETITOR = "COMPETITOR",
}

export const campaignFormDefinition = (t: TFunction) =>
  [
    {
      fieldType: "header",
      label: t("predictive.campaign.questionnaire.details.label"),
      description: t("predictive.campaign.questionnaire.description"),
      step: 1,
    },
    {
      fieldType: "warning",
      label: t("predictive.campaign.questionnaire.warning.label"),
      description: t("predictive.campaign.questionnaire.warning.description"),
      step: 1,
    },
    {
      name: "name",
      label: t("predictive.campaign.questionnaire.name.label"),
      fieldType: "input",
      placeholder: t("predictive.campaign.questionnaire.name.placeholder"),
      rules: { required: t("predictive.campaign.questionnaire.name.validation") },
      step: 1,
    },
    {
      name: "subscription",
      label: t("predictive.campaign.questionnaire.subscription.label"),
      fieldType: "subscriptionSelect",
      rules: { required: t("predictive.campaign.questionnaire.subscription.validation") },
      step: 1,
    },
    {
      fieldType: "header",
      label: t("predictive.campaign.questionnaire.audienceHeader.label"),
      description: t("predictive.campaign.questionnaire.audienceHeader.description"),
      step: 2,
    },
    {
      name: target.REGION_COUNTRY,
      fieldType: "locationSelect",
      placeholder: t("predictive.campaign.questionnaire.regionCountry.placeholder"),
      rules: { required: t("predictive.campaign.questionnaire.regionCountry.validation") },
      step: 2,
      target: target.REGION_COUNTRY,
      config: {
        resultItems: ["COUNTRY_CODE"],
        filter: {
          included_country_codes: ["DE", "AT"],
        },
      },
      serialize: (v) => ({
        type: "TEXT" as PredictiveAnswerTypeDto.TEXT,
        value: v.country_code,
      }),
    },
    {
      name: target.REGION_COUNTRY_INCLUDE,
      fieldType: "regionSelect",
      placeholder: t("predictive.campaign.questionnaire.regionInclude.placeholder"),
      rules: { required: t("predictive.campaign.questionnaire.regionInclude.validation") },
      step: 2,
      target: target.REGION_COUNTRY_INCLUDE,
      serialize: (v) => ({
        type: "MULTIPLE_CHOICE" as PredictiveAnswerTypeDto.MULTIPLE_CHOICE,
        values: v.map(({ country_code, target, name }) => ({
          country_code,
          target,
          name,
        })),
      }),
    },
    {
      name: target.REGION_COUNTRY_EXCLUDE,
      fieldType: "regionSelect",
      placeholder: t("predictive.campaign.questionnaire.regionExclude.placeholder"),
      step: 2,
      target: target.REGION_COUNTRY_EXCLUDE,
      isOptional: true,
      serialize: (v) => ({
        type: "MULTIPLE_CHOICE" as PredictiveAnswerTypeDto.MULTIPLE_CHOICE,
        values: v.map(({ country_code, target, name }) => ({
          country_code,
          target,
          name,
        })),
      }),
    },
    {
      name: target.INDUSTRY_INCLUDE,
      fieldType: "naceSelect",
      placeholder: t("predictive.campaign.questionnaire.industryInclude.placeholder"),
      rules: { required: t("predictive.campaign.questionnaire.industryInclude.validation") },
      step: 2,
      target: target.INDUSTRY_INCLUDE,
      serialize: (v) => ({
        type: "MULTIPLE_CHOICE" as PredictiveAnswerTypeDto.MULTIPLE_CHOICE,
        values: v.map(({ id }) => ({
          id,
          target: "NACE",
        })),
      }),
    },
    {
      name: target.INDUSTRY_EXCLUDE,
      fieldType: "naceSelect",
      placeholder: t("predictive.campaign.questionnaire.industryExclude.placeholder"),
      step: 2,
      target: target.INDUSTRY_EXCLUDE,
      isOptional: true,
      serialize: (v) => ({
        type: "MULTIPLE_CHOICE" as PredictiveAnswerTypeDto.MULTIPLE_CHOICE,
        values: v.map(({ id }) => ({
          id,
          target: "NACE",
        })),
      }),
    },
    {
      name: target.AUDIENCE_CHARACTERISTICS,
      fieldType: "input",
      placeholder: t("predictive.campaign.questionnaire.audienceCharacteristics.placeholder"),
      rules: { required: t("predictive.campaign.questionnaire.audienceCharacteristics.validation") },
      helperText: t("predictive.campaign.questionnaire.audienceCharacteristics.helperText"),
      step: 2,
      target: target.AUDIENCE_CHARACTERISTICS,
    },
    {
      name: target.HEADCOUNT,
      fieldType: "checkboxes",
      rules: { required: t("predictive.campaign.questionnaire.headcount.validation"), minLength: 1 },
      step: 2,
      target: target.HEADCOUNT,
    },
    {
      name: target.REVENUE,
      fieldType: "checkboxes",
      step: 2,
      target: target.REVENUE,
      isOptional: true,
    },
    {
      name: target.COMPANY_AGE,
      fieldType: "checkboxes",
      step: 2,
      target: target.COMPANY_AGE,
      isOptional: true,
    },
    {
      name: target.CONTACT_PERSON_ROLE_INCLUDE,
      fieldType: "comboBoxMultiple",
      rules: { required: t("predictive.campaign.questionnaire.contactPersonRoleInclude.validation") },
      helperText: t("predictive.campaign.questionnaire.contactPersonRoleInclude.helperText"),
      step: 2,
      target: target.CONTACT_PERSON_ROLE_INCLUDE,
    },
    {
      name: target.CONTACT_PERSON_ROLE_EXCLUDE,
      helperText: t("predictive.campaign.questionnaire.contactPersonRoleExclude.helperText"),
      fieldType: "comboBoxMultiple",
      step: 2,
      target: target.CONTACT_PERSON_ROLE_EXCLUDE,
      isOptional: true,
    },
    {
      fieldType: "header",
      label: t("predictive.campaign.questionnaire.insightsHeader.label"),
      description: t("predictive.campaign.questionnaire.insightsHeader.description"),
      step: 3,
    },
    {
      name: target.SIGNAL_INCLUDE,
      fieldType: "textarea",
      placeholder: t("predictive.campaign.questionnaire.signalInclude.placeholder"),
      step: 3,
      rules: { required: t("predictive.campaign.questionnaire.signalInclude.validation") },
      helperText: t("predictive.campaign.questionnaire.signalInclude.helperText"),
      target: target.SIGNAL_INCLUDE,
    },
    {
      name: target.SIGNAL_EXCLUDE,
      fieldType: "textarea",
      placeholder: t("predictive.campaign.questionnaire.signalExclude.placeholder"),
      step: 3,
      helperText: t("predictive.campaign.questionnaire.signalExclude.helperText"),
      target: target.SIGNAL_EXCLUDE,
      isOptional: true,
    },
    {
      name: target.TOOL,
      fieldType: "comboBoxMultiple",
      step: 3,
      helperText: t("predictive.campaign.questionnaire.tool.helperText"),
      target: target.TOOL,
      isOptional: true,
    },
    {
      name: target.CLIENT,
      fieldType: "companies",
      step: 3,
      target: target.CLIENT,
      isOptional: true,
    },
    {
      name: target.COMPETITOR,
      fieldType: "companies",
      step: 3,
      target: target.COMPETITOR,
      isOptional: true,
    },
  ] as const satisfies readonly FormConfigField[];

type CampaignFormDefinition = ReturnType<typeof campaignFormDefinition>;
export type CampaignFormValues = FormValues<CampaignFormDefinition>;
